const TOKEN_KEY = 'google_drive_token';
const EXPIRES_KEY = 'google_drive_expires_at';
const REDIRECT_KEY = 'google_drive_post_login_redirect';

export interface GoogleAuthState {
  accessToken: string;
  expiresAt?: number;
}

export function loginWithGoogle(returnTo?: string): void {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  if (!clientId) {
    throw new Error('Google client ID is not configured.');
  }

  const redirectTarget =
    returnTo ?? window.location.pathname + window.location.search + window.location.hash;
  sessionStorage.setItem(REDIRECT_KEY, redirectTarget);

  const redirectUri = `${window.location.origin}/callback/google`;
  const scope = 'https://www.googleapis.com/auth/drive';
  const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
  authUrl.searchParams.set('client_id', clientId);
  authUrl.searchParams.set('redirect_uri', redirectUri);
  authUrl.searchParams.set('response_type', 'code');
  authUrl.searchParams.set('scope', scope);
  authUrl.searchParams.set('access_type', 'offline');
  authUrl.searchParams.set('prompt', 'consent');
  authUrl.searchParams.set('state', encodeURIComponent(redirectTarget));

  window.location.assign(authUrl.toString());
}

export interface GoogleCallbackResult {
  token?: string;
  code?: string;
  error?: string;
}

export function handleGoogleCallback(): GoogleCallbackResult {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') ?? params.get('access_token') ?? undefined;
  const code = params.get('code') ?? undefined;
  const error = params.get('error') ?? undefined;

  if (token) {
    setGoogleAuth({ accessToken: token });
  }

  return { token, code, error };
}

export function getGoogleRedirectFromState(): string | null {
  const params = new URLSearchParams(window.location.search);
  const state = params.get('state');
  if (!state) {
    return null;
  }
  try {
    return decodeURIComponent(state);
  } catch {
    return state;
  }
}

export function getGoogleAuth(): GoogleAuthState | null {
  const accessToken = sessionStorage.getItem(TOKEN_KEY);
  if (!accessToken) {
    return null;
  }
  const expiresAtRaw = sessionStorage.getItem(EXPIRES_KEY);
  const expiresAt = expiresAtRaw ? Number(expiresAtRaw) : undefined;

  return {
    accessToken,
    expiresAt: Number.isFinite(expiresAt) ? expiresAt : undefined,
  };
}

export function getGoogleToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function setGoogleAuth(state: GoogleAuthState): void {
  sessionStorage.setItem(TOKEN_KEY, state.accessToken);
  if (state.expiresAt) {
    sessionStorage.setItem(EXPIRES_KEY, String(state.expiresAt));
  } else {
    sessionStorage.removeItem(EXPIRES_KEY);
  }
  window.dispatchEvent(new CustomEvent('google-auth-change'));
}

export function clearGoogleAuth(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(EXPIRES_KEY);
  window.dispatchEvent(new CustomEvent('google-auth-change'));
}

export function isGoogleAuthenticated(): boolean {
  return Boolean(getGoogleToken());
}

export function consumeGoogleRedirect(): string | null {
  const redirect = sessionStorage.getItem(REDIRECT_KEY);
  if (redirect) {
    sessionStorage.removeItem(REDIRECT_KEY);
  }
  return redirect;
}
